/**
 * CR-7274: "Configure settings for" — the selector and the inheritance badge.
 *
 * The ticket's finding #1 was that this screen had no scope picker and nothing
 * naming the company being edited, so an admin with eight plants typed every
 * target eight times and could not tell which copy they were looking at.
 *
 * This is the surface that fixes it:
 *   - choose Group Default, or one site
 *   - see whether the values shown are this scope's own or inherited, and from
 *     where
 *   - clear a site's override so it goes back to following the group
 *
 * The badge is not decoration. "Inherited from Acme Group" versus "Overridden
 * for Plant A" is the difference the customer could not see, and it is what
 * makes a drifted plant visible.
 */
import { Building2, CornerDownRight, Loader2, RotateCcw } from 'lucide-react';

import type { SettingsScope } from '../../../lib/api';
import type { ScopeSelection } from '../../../lib/api/performanceSettings';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';

/** One choice in the picker. */
export interface ScopeOption {
  /** Stable key for the <Select>; encodes the scope and id. */
  value: string;
  label: string;
  selection: ScopeSelection;
}

export interface SettingsScopeSelectorProps {
  options: ScopeOption[];
  value: string;
  onChange: (option: ScopeOption) => void;
  /** Where the values on screen actually came from. */
  scope?: SettingsScope;
  /** The site, group or "Platform Default" that owns them. */
  owner?: string;
  /**
   * The kind of scope the picker is CONFIGURING, as opposed to where the
   * values came from. A Group Default being looked at directly resolves to
   * `scope === 'group'` too, and without this the badge read "Inherited from
   * Acme" over the group's own row (tester observation 1, 20 Oct 2026).
   */
  configuring?: SettingsScope;
  /** True while settings for the chosen scope are loading. */
  loading?: boolean;
  /** Shown only when the chosen scope has an override to clear. */
  onClearOverride?: () => void;
  clearing?: boolean;
  disabled?: boolean;
}

/** A site as the picker needs it: its own identity plus the group above it. */
export interface ScopeSite {
  id: number;
  name: string;
  group?: number | null;
  group_name?: string | null;
}

/**
 * Build the picker's options: every Group Default first, then each site.
 *
 * Group Defaults lead because they are what an admin should reach for — the
 * edit that covers every plant. A site is the exception, not the default.
 *
 * ONE ENTRY PER DISTINCT GROUP, not one overall. A customer admin sees only
 * their own group's sites, so they get a single "Group Default". A Helix admin
 * sees every tenant, and collapsing that to one entry meant offering whichever
 * group happened to sort first — so configuring Acme Manufacturing presented
 * "API Test Group — Group Default", and saving would have written to an
 * unrelated tenant. Found in the browser; no unit test would have caught it,
 * because with one tenant's fixtures the two behave identically.
 */
export function buildScopeOptions(sites: ScopeSite[]): ScopeOption[] {
  const groups = new Map<number, string>();
  sites.forEach((site) => {
    if (site.group && site.group_name && !groups.has(site.group)) {
      groups.set(site.group, site.group_name);
    }
  });

  const options: ScopeOption[] = [];
  Array.from(groups.entries())
    .sort((a, b) => a[1].localeCompare(b[1]))
    .forEach(([id, name]) => {
      options.push({
        value: `group:${id}`,
        label: `${name} — Group Default`,
        selection: { scope: 'group', id },
      });
    });
  sites.forEach((site) => {
    options.push({
      value: `site:${site.id}`,
      label: site.name,
      selection: { scope: 'site', id: site.id },
    });
  });
  return options;
}

/**
 * The badge text and tone for a resolved scope.
 *
 * `configuring` is what the picker points at. When that is a group and the
 * values came from a group row, the row IS the Group Default — it is not
 * inheriting from itself — so the badge says what it is and who it applies
 * to rather than "Inherited from <the same group>".
 */
export function describeScope(
  scope?: SettingsScope,
  owner?: string,
  configuring?: SettingsScope,
): { label: string; inherited: boolean } | null {
  if (!scope) return null;
  if (scope === 'site') {
    return { label: 'Overridden for this site', inherited: false };
  }
  if (scope === 'group') {
    if (configuring === 'group') {
      return {
        label: 'Group Default — applies to sites without their own settings',
        inherited: false,
      };
    }
    return { label: `Inherited from ${owner ?? 'the group'}`, inherited: true };
  }
  return { label: 'Inherited from platform default', inherited: true };
}

/** True when the configured scope has a row of its own that can be cleared. */
export function canClearScope(scope?: SettingsScope, configuring?: SettingsScope): boolean {
  if (scope === 'site') return true;
  // A Group Default is the group's own row; clearing it makes every
  // inheriting site fall back to the platform default (tester observation 2).
  return scope === 'group' && configuring === 'group';
}

export default function SettingsScopeSelector({
  options,
  value,
  onChange,
  scope,
  owner,
  configuring,
  loading = false,
  onClearOverride,
  clearing = false,
  disabled = false,
}: SettingsScopeSelectorProps) {
  const description = describeScope(scope, owner, configuring);
  // Only a scope that has its OWN row has anything to clear. Offering it on
  // an inherited scope would promise something the button cannot do.
  const canClear = Boolean(onClearOverride) && canClearScope(scope, configuring);
  const clearLabel = configuring === 'group' ? 'Clear Group Default' : 'Clear override';

  const handleChange = (next: string) => {
    const option = options.find((candidate) => candidate.value === next);
    if (option) onChange(option);
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex shrink-0 items-center gap-2 whitespace-nowrap text-sm font-medium">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span>Configure settings for</span>
        </div>
        <Select value={value} onValueChange={handleChange} disabled={disabled || loading}>
          <SelectTrigger className="w-[280px]" aria-label="Configure settings for">
            <SelectValue placeholder="Select a scope" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {/* The badge must not shrink: as a flex child it was squeezed to a
          narrow pill and the Group Default text wrapped over eight lines
          (UAT 27 Oct). The row wraps instead. */}
      <div className="flex flex-wrap items-center gap-3 sm:justify-end">
        {description && (
          <Badge
            variant={description.inherited ? 'secondary' : 'default'}
            className="shrink-0 gap-1 whitespace-nowrap"
          >
            {description.inherited && <CornerDownRight className="h-3 w-3" />}
            {description.label}
          </Badge>
        )}
        {canClear && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearOverride}
            disabled={clearing}
            className="gap-1"
          >
            {clearing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RotateCcw className="h-3 w-3" />
            )}
            {clearLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
